import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';
import StepUpDialog from '../components/StepUpDialog';
import { isStepUpRequired } from '../utils/stepUp';

export const STEP_UP_ACTIONS = {
    CERTIFICATE_REVOKE: 'لغو گواهی',
    CERTIFICATE_CSR_DOWNLOAD: 'دریافت گواهی',
    TERMINATE_ALL_SESSIONS: 'خروج از همه دستگاه‌ها',
    MFA_CHANGE: 'تغییر احراز هویت دو مرحله‌ای',
    PASSKEY_CHANGE: 'تغییر Passkey',
    RECOVERY_CODES: 'تولید کدهای بازیابی',
};

const STEP_UP_TOKEN_MAX_AGE = 540; // slightly less than the server TTL (600s)

const StepUpContext = createContext({
    runWithStepUp: () => Promise.reject(new Error('StepUpProvider is missing')),
    requestStepUp: () => Promise.reject(new Error('StepUpProvider is missing')),
});

// In-memory only cache for step-up tokens. Previously sessionStorage was used,
// but that made tokens JS-readable to any XSS and cross-tab. Single-use +
// short TTL already limit impact, but memory-only reduces blast radius.
const memoryCache = new Map(); // action -> {token, expiresAt}

const readCachedToken = (action) => {
    const entry = memoryCache.get(action);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
        memoryCache.delete(action);
        return null;
    }
    return entry.token;
};

const cacheToken = (action, token) => {
    memoryCache.set(action, {
        token,
        expiresAt: Date.now() + STEP_UP_TOKEN_MAX_AGE * 1000,
    });
};

export const clearCachedStepUpToken = () => {
    memoryCache.clear();
};

/**
 * Orchestrates Step-Up authentication:
 *  - Attempts the action first (unless a cached token exists); on 403
 *    STEP_UP_REQUIRED it opens the verification dialog.
 *  - On successful verification the action is retried with the token.
 *
 * `actionFn` receives the step-up token as its single argument.
 */
export const StepUpProvider = ({ children }) => {
    const [dialog, setDialog] = useState(null);
    const pendingRef = useRef(null);

    const requestStepUp = useCallback((action, verifyContext = {}) => {
        const cached = readCachedToken(action);
        if (cached) {
            return Promise.resolve(cached);
        }

        return new Promise((resolve, reject) => {
            pendingRef.current = { resolve, reject };
            setDialog({ action, ...verifyContext });
        });
    }, []);

    const runWithStepUp = useCallback(
        async (action, actionFn) => {
            const cached = readCachedToken(action);
            try {
                if (cached) {
                    return await actionFn(cached);
                }
                return await actionFn(undefined);
            } catch (error) {
                const stepUpInfo = isStepUpRequired(error, action);
                if (!stepUpInfo) {
                    throw error;
                }
                // The cached token was rejected (expired/invalid), so drop it to
                // guarantee the verification dialog is shown instead of a silent
                // retry loop that reuses the same stale token.
                clearCachedStepUpToken();
                const token = await requestStepUp(stepUpInfo.action, {
                    requireNewVerification: true,
                });
                return await actionFn(token);
            }
        },
        [requestStepUp],
    );

    const handleSuccess = useCallback((action, token) => {
        cacheToken(action, token);
        if (pendingRef.current) {
            pendingRef.current.resolve(token);
            pendingRef.current = null;
        }
        setDialog(null);
    }, []);

    const handleClose = useCallback(() => {
        if (pendingRef.current) {
            pendingRef.current.reject(new Error('STEP_UP_CANCELLED'));
            pendingRef.current = null;
        }
        setDialog(null);
    }, []);

    const value = useMemo(
        () => ({ runWithStepUp, requestStepUp }),
        [runWithStepUp, requestStepUp],
    );

    return (
        <StepUpContext.Provider value={value}>
            {children}
            {dialog && (
                <StepUpDialog
                    action={dialog.action}
                    requireNewVerification={dialog.requireNewVerification}
                    onSuccess={(token) => handleSuccess(dialog.action, token)}
                    onClose={handleClose}
                />
            )}
        </StepUpContext.Provider>
    );
};

export const useStepUp = () => useContext(StepUpContext);

export default StepUpProvider;